import React, { useState, useEffect, useCallback } from 'react'
import {
  CCard, CModal,
  CCardBody, CModalHeader,
  CButton, CModalBody,
  CCardHeader, CDropdown,
  CCol, CLabel,
  CRow, CFormGroup, CInput, CBadge,
} from 'compat/coreui'

import axios from 'axios';
import moment from 'moment';
import { postData } from '../../../helpers/api';
import { toast } from 'react-toastify';

const STATUS_FLOW = ["Pending", "Accepted", "Processing", "Shipped", "Delivered", "Cancelled"];
const statusColor = (s) => ({
  Pending: 'secondary', Accepted: 'info', Processing: 'warning',
  Shipped: 'primary', Delivered: 'success', Cancelled: 'danger',
}[s] || 'secondary');

const Orders = () => {
  const [orderData, setOrderData] = useState([]);
  const [statusModal, setStatusModal] = useState(false);
  const [shipModal, setShipModal] = useState(false);
  const [orderStatus, setOrderStatus] = useState('Accepted');
  const [orderId, setOrderId] = useState('');
  const [shipGroup, setShipGroup] = useState(null); // the group being edited
  const [shipForm, setShipForm] = useState({ carrier: '', trackingNumber: '', trackingUrl: '', eventStatus: '', eventLocation: '' });
  const [saving, setSaving] = useState(false);

  const getData = useCallback(() => {
    axios.get(`${process.env.REACT_APP_APP_API_URL}/api/order`, {
      headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
    }).then(res => {
      setOrderData(res.data.orders || []);
    }).catch(() => {})
  }, []);

  useEffect(() => { getData() }, [getData])

  // group lines by checkout
  const groups = [];
  const byId = {};
  for (const o of orderData) {
    const key = o.orderGroupId || o._id;
    if (!byId[key]) { byId[key] = { key, lines: [], createdAt: o.createdAt }; groups.push(byId[key]); }
    byId[key].lines.push(o);
  }

  const openStatus = (id, current) => {
    setOrderId(id);
    setOrderStatus(current === 'Pending' ? 'Accepted' : current);
    setStatusModal(true);
  };

  const openShipment = (group) => {
    const first = group.lines[0] || {};
    const s = first.shipment || {};
    setShipGroup(group);
    setShipForm({
      carrier: s.carrier || '', trackingNumber: s.trackingNumber || '',
      trackingUrl: s.trackingUrl || '', eventStatus: '', eventLocation: '',
    });
    setShipModal(true);
  };

  const statusHandler = (e) => {
    e.preventDefault();
    postData(`${process.env.REACT_APP_APP_API_URL}/api/order/status`, { orderId, status: orderStatus }).then(res => {
      if (res.data.status) {
        setStatusModal(false);
        toast.success('Order status updated');
        getData();
      } else {
        toast.error(res.data.message || 'Could not update the status');
      }
    }).catch(() => toast.error('Could not update the status'))
  }

  const shipmentHandler = (e, markShipped) => {
    e.preventDefault();
    if (!shipGroup) return;
    setSaving(true);
    const body = {
      orderGroupId: shipGroup.lines[0].orderGroupId || shipGroup.key,
      carrier: shipForm.carrier,
      trackingNumber: shipForm.trackingNumber,
      trackingUrl: shipForm.trackingUrl,
      markShipped: !!markShipped,
    };
    if (shipForm.eventStatus) {
      body.event = { status: shipForm.eventStatus, location: shipForm.eventLocation };
    }
    postData(`${process.env.REACT_APP_APP_API_URL}/api/order/shipment`, body).then(res => {
      if (res.data.status) {
        setShipModal(false);
        toast.success(markShipped ? 'Marked as shipped' : 'Shipment updated');
        getData();
      } else {
        toast.error(res.data.message || 'Could not update the shipment');
      }
    }).catch(() => toast.error('Could not update the shipment')).finally(() => setSaving(false))
  }

  return (
    <CRow>
      <CCol xl={12}>
        <CCard>
          <CCardHeader className="category_header">
            Orders
          </CCardHeader>
          <CCardBody>
            {groups.length === 0 && <p className="text-muted mb-0">No orders yet.</p>}
            {groups.map((group) => {
              const first = group.lines[0];
              const total = group.lines.reduce((s, l) => s + (l.lineTotal || l.price * l.quantity || 0), 0);
              const addr = first.deliveryAddress;
              const ship = first.shipment;
              return (
                <CCard key={group.key} className="mb-4">
                  <CCardHeader className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <div>
                      <strong>Order {String(group.key).slice(-8)}</strong>
                      <span className="text-muted ml-2">{moment(group.createdAt).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                      {first.paymentMethod === 'BRC'
                        ? <CBadge color={first.paymentStatus === 'paid' ? 'success' : 'warning'}>
                            {first.paymentStatus === 'paid' ? '⛓ Paid in BRC' : 'BRC — awaiting payment'}
                          </CBadge>
                        : <CBadge color="info">Cash on delivery</CBadge>}
                      <CButton size="sm" color="primary" onClick={() => openShipment(group)}>Shipment</CButton>
                    </div>
                  </CCardHeader>
                  <CCardBody>
                    <div className="table-responsive">
                      <table className="table table-hover mb-2">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Unit Price</th>
                            <th>Qty</th>
                            <th>Line Total</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.lines.map((line) => (
                            <tr key={line._id}>
                              <td>{line.product ? line.product.name : '—'}</td>
                              <td>${line.price}</td>
                              <td>{line.quantity}</td>
                              <td>${(line.lineTotal || line.price * line.quantity).toFixed ? (line.lineTotal || line.price * line.quantity).toFixed(2) : line.lineTotal}</td>
                              <td>
                                <CButton size="sm" className="status_button" color={statusColor(line.status)} onClick={() => openStatus(line._id, line.status)}>
                                  {line.status}
                                </CButton>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <CRow>
                      <CCol md={5}>
                        <strong>Deliver to:</strong>{' '}
                        {addr && addr.name
                          ? <span>{addr.name}, {addr.street}, {addr.city}{addr.country ? `, ${addr.country}` : ''} · {addr.phone}</span>
                          : <span className="text-muted">{first.buyer ? `${first.buyer.username} (${first.buyer.email})` : '—'}</span>}
                        {addr && addr.notes && <div className="small text-muted">Note: {addr.notes}</div>}
                      </CCol>
                      <CCol md={4}>
                        {ship && (ship.carrier || ship.trackingNumber)
                          ? <span><strong>Tracking:</strong> {ship.carrier} {ship.trackingNumber}</span>
                          : <span className="text-muted">No tracking info yet</span>}
                      </CCol>
                      <CCol md={3} className="text-right">
                        <strong>Total: ${total.toFixed(2)}</strong>
                      </CCol>
                    </CRow>
                  </CCardBody>
                </CCard>
              );
            })}
          </CCardBody>
        </CCard>
      </CCol>

      <CModal show={statusModal} onClose={() => setStatusModal(false)} className="order_modal">
        <CModalHeader closeButton>Change Order Status</CModalHeader>
        <CModalBody>
          <form onSubmit={statusHandler}>
            <CFormGroup>
              <CLabel>Order status</CLabel>
              <CDropdown>
                <select className="custom-select my-1 mr-sm-2 product_status" value={orderStatus} onChange={(e) => setOrderStatus(e.target.value)}>
                  {STATUS_FLOW.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </CDropdown>
            </CFormGroup>
            <CButton color="success" className="mt-4" type="submit">Update Status</CButton>{' '}
            <CButton color="secondary" size="lg" className="mt-4 pl-3 pr-3" onClick={() => setStatusModal(false)}>
              Cancel
            </CButton>
          </form>
        </CModalBody>
      </CModal>

      <CModal show={shipModal} onClose={() => setShipModal(false)} className="order_modal">
        <CModalHeader closeButton>Shipment & Tracking</CModalHeader>
        <CModalBody>
          <form onSubmit={shipmentHandler}>
            <CFormGroup className="row px-3">
              <CCol md={6} className="pl-0">
                <CLabel>Carrier</CLabel>
                <CInput placeholder="e.g. DHL, EMS, Ethiopian Post" value={shipForm.carrier}
                  onChange={e => setShipForm(f => ({ ...f, carrier: e.target.value }))} />
              </CCol>
              <CCol md={6} className="pr-0">
                <CLabel>Tracking Number</CLabel>
                <CInput placeholder="Tracking #" value={shipForm.trackingNumber}
                  onChange={e => setShipForm(f => ({ ...f, trackingNumber: e.target.value }))} />
              </CCol>
            </CFormGroup>
            <CFormGroup>
              <CLabel>Tracking URL (optional)</CLabel>
              <CInput placeholder="https://…" value={shipForm.trackingUrl}
                onChange={e => setShipForm(f => ({ ...f, trackingUrl: e.target.value }))} />
            </CFormGroup>
            <hr />
            <CFormGroup className="row px-3">
              <CCol md={6} className="pl-0">
                <CLabel>Add Progress Update</CLabel>
                <CInput placeholder="e.g. Package handed to carrier" value={shipForm.eventStatus}
                  onChange={e => setShipForm(f => ({ ...f, eventStatus: e.target.value }))} />
              </CCol>
              <CCol md={6} className="pr-0">
                <CLabel>Location (optional)</CLabel>
                <CInput placeholder="e.g. Addis Ababa hub" value={shipForm.eventLocation}
                  onChange={e => setShipForm(f => ({ ...f, eventLocation: e.target.value }))} />
              </CCol>
            </CFormGroup>
            {shipGroup && shipGroup.lines[0] && shipGroup.lines[0].shipment && (shipGroup.lines[0].shipment.events || []).length > 0 && (
              <div className="mb-3">
                <CLabel>History</CLabel>
                <ul className="pl-3 mb-0 small">
                  {shipGroup.lines[0].shipment.events.slice().reverse().map((ev, i) => (
                    <li key={i}>{moment(ev.at).format('MM-DD HH:mm')} — {ev.status}{ev.location ? ` (${ev.location})` : ''}</li>
                  ))}
                </ul>
              </div>
            )}
            <CButton color="success" type="submit" disabled={saving}>Save</CButton>{' '}
            <CButton color="primary" disabled={saving} onClick={(e) => shipmentHandler(e, true)}>Save & Mark Shipped</CButton>{' '}
            <CButton color="secondary" onClick={() => setShipModal(false)}>Cancel</CButton>
          </form>
        </CModalBody>
      </CModal>
    </CRow>
  )
}

export default Orders;
